import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../config/axios';

interface Branch {
  branch_id: number;
  branch_name: string;
  address: string;
  phone: string;
  email: string;
  manager_name: string;
  created_at: string;
}

interface BranchesState {
  branches: Branch[];
  loading: boolean;
  error: string | null;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const initialState: BranchesState = {
  branches: [],
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
};

export const fetchBranches = createAsyncThunk(
  'branches/fetchBranches',
  async (params: { page?: number; limit?: number; search?: string } = {}) => {
    const { page = 1, limit = 10, search = '' } = params;
    const response = await apiClient.get('/branches', {
      params: { page, limit, search }
    });
    return response.data;
  }
);

export const createBranch = createAsyncThunk(
  'branches/createBranch',
  async (branchData: Omit<Branch, 'branch_id' | 'created_at'>) => {
    const response = await apiClient.post('/branches', branchData);
    return response.data;
  }
);

export const updateBranch = createAsyncThunk(
  'branches/updateBranch',
  async ({ id, branchData }: { id: number; branchData: Omit<Branch, 'branch_id' | 'created_at'> }) => {
    const response = await apiClient.put(`/branches/${id}`, branchData);
    return response.data.data; // Extract the actual branch data from the response
  }
);

export const deleteBranch = createAsyncThunk(
  'branches/deleteBranch',
  async (id: number) => {
    const response = await apiClient.delete(`/branches/${id}`);
    return { id, message: response.data.message };
  }
);

const branchesSlice = createSlice({
  name: 'branches',
  initialState,
  reducers: {
    addBranchToCollection: (state, action) => {
      const branch = action.payload;
      // Check if branch already exists
      const existingBranch = state.branches.find(b => b.branch_id === branch.branch_id);
      if (!existingBranch) {
        state.branches.unshift(branch); // Add to beginning of array
        state.pagination.total += 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBranches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch branches';
      })
      .addCase(createBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.branches.push(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create branch';
      })
      .addCase(updateBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBranch.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.branches.findIndex(branch => branch.branch_id === action.payload.branch_id);
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update branch';
      })
      .addCase(deleteBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = state.branches.filter(branch => branch.branch_id !== action.payload.id);
        state.pagination.total -= 1;
      })
      .addCase(deleteBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete branch';
      });
  },
});

export const { addBranchToCollection } = branchesSlice.actions;
export default branchesSlice.reducer;
